import { checkAuthThunk } from "@/features/auth/redux/auth.slice";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { logError } from "@/utils/error";
import { Suspense, lazy, useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import "./App.css";
import ProtectedRoute from "./components/ProtectedRoute";
import { RouteFallback } from "./components/RouteFallback";
import { ScrollToTop } from "./components/ScrollToTop";
import { LoadingState } from "./components/ui-template/LoadingState";
import { ThemeProvider } from "./styles/themes/theme-provider";

// Public routes
const LandingPage = lazy(() => import("@/features/LandingPage"));
const DownloadPage = lazy(() =>
  import("@/features/LandingPage/Download").then((module) => ({
    default: module.DownloadPage,
  }))
);
const ProductPage = lazy(() =>
  import("@/features/LandingPage/Product").then((module) => ({
    default: module.ProductPage,
  }))
);
const WorkflowPage = lazy(() =>
  import("@/features/LandingPage/Workflow").then((module) => ({
    default: module.WorkflowPage,
  }))
);
const NewsPage = lazy(() =>
  import("@/features/LandingPage/News").then((module) => ({
    default: module.NewsPage,
  }))
);
const NewsDetailPage = lazy(() =>
  import("@/features/LandingPage/NewsDetail").then((module) => ({
    default: module.NewsDetailPage,
  }))
);
const PricingPage = lazy(() =>
  import("@/features/LandingPage/Pricing").then((module) => ({
    default: module.PricingPage,
  }))
);
const LoginScreen = lazy(
  () => import("@/features/auth/screens/LoginScreen/LoginScreen")
);

// Protected routes
const Default = lazy(() => import("./components/layout/Default"));

function App() {
  const dispatch = useAppDispatch();
  const { user, loginSuccess } = useAppSelector((state) => state.auth);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        setIsCheckingAuth(true);
        await dispatch(checkAuthThunk());
      } catch (error) {
        logError("App.checkAuth", error);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuth();
  }, [dispatch]);

  if (isCheckingAuth) {
    return (
      <ThemeProvider defaultTheme="light">
        <LoadingState
          variant="page"
          message="Đang kiểm tra đăng nhập…"
        />
      </ThemeProvider>
    );
  }

  const isAuthenticated = Boolean(user) || loginSuccess;

  return (
    <ThemeProvider defaultTheme="light">
      <ScrollToTop />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route
            path="/home"
            element={<LandingPage />}
          />
          <Route
            path="/download"
            element={<DownloadPage />}
          />
          <Route
            path="/product"
            element={<ProductPage />}
          />
          <Route
            path="/workflow"
            element={<WorkflowPage />}
          />
          <Route
            path="/news/:slug"
            element={<NewsDetailPage />}
          />
          <Route
            path="/news"
            element={<NewsPage />}
          />
          <Route
            path="/pricing"
            element={<PricingPage />}
          />
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate
                  to="/"
                  replace
                />
              ) : (
                <LoginScreen />
              )
            }
          />

          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Default />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
    </ThemeProvider>
  );
}

export default App;
